import { AnimatePresence, motion } from "framer-motion";
import { useAudit } from "./AuditContext";

/**
 * <Presenter pose="thinking" />
 *
 * Renders the currently selected presenter (from AuditContext) in a fixed
 * viewport box and cross-fades between poses using Framer Motion.
 *
 * The speech bubble is rendered separately by <Stage> as a viewport-fixed
 * overlay OUTSIDE the camera transform so the bubble text stays crisp,
 * readable, and predictably positioned regardless of the camera zoom.
 *
 * Unknown poses fall back to `neutral` so callers never have to guard.
 */

const FALLBACK_POSE = "neutral";

const POSE_TRANSITION = {
  duration: 0.55,
  ease: [0.16, 1, 0.3, 1],
};

export function Presenter({
  pose = "neutral",
  size = "md", // "sm" | "md" | "lg"
  className = "",
  testId = "presenter",
}) {
  const { selectedPresenter } = useAudit();
  if (!selectedPresenter) return null;

  const { avatarBase, poses = {}, name } = selectedPresenter;
  const file = poses[pose] || poses[FALLBACK_POSE];
  const src = `${avatarBase}/${file}`;

  const boxSize = {
    sm: "w-[180px] h-[260px] md:w-[220px] md:h-[320px]",
    md: "w-[240px] h-[340px] md:w-[300px] md:h-[420px]",
    lg: "w-[300px] h-[420px] md:w-[380px] md:h-[520px]",
  }[size] || "w-[240px] h-[340px] md:w-[300px] md:h-[420px]";

  return (
    <div
      className={`relative ${className}`}
      data-testid={testId}
    >
      <div className={`relative ${boxSize}`}>
        <AnimatePresence initial={false} mode="popLayout">
          <motion.img
            key={`${selectedPresenter.id}:${pose}`}
            src={src}
            alt={`${name} — ${pose}`}
            draggable={false}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={POSE_TRANSITION}
            className="absolute inset-0 w-full h-full object-contain select-none"
            style={{ mixBlendMode: "multiply" }}
            onError={(e) => {
              if (!e.currentTarget.dataset.fallback) {
                e.currentTarget.dataset.fallback = "1";
                e.currentTarget.src = `${avatarBase}/${poses[FALLBACK_POSE]}`;
              }
            }}
            data-testid={`${testId}-image-${pose}`}
          />
        </AnimatePresence>
      </div>
    </div>
  );
}

/**
 * <PresenterBubble> — viewport-fixed speech bubble.
 * Rendered by <Stage> OUTSIDE the camera transform so it never scales with
 * the room zoom. The bubble anchor is passed as viewport percentages so
 * different cameras can point the tail toward the presenter's mouth.
 */
export function PresenterBubble({
  message = "",
  anchor = { topPct: 10, leftPct: 12, widthPx: 300 },
  testId = "presenter-bubble",
}) {
  return (
    <div className="absolute inset-0 pointer-events-none hidden md:block" data-testid={`${testId}-layer`}>
      <AnimatePresence initial={false}>
        {message ? (
          <motion.div
            key={message}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute z-30"
            style={{
              top: `${anchor.topPct}%`,
              left: `${anchor.leftPct}%`,
              width: `${anchor.widthPx}px`,
              transform: "translateX(-50%)",
            }}
            data-testid={testId}
          >
            <div
              className="relative bg-white text-[#1B1B18] px-4 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
              style={{ borderRadius: "22px" }}
            >
              <p className="font-serif text-sm md:text-base leading-snug">{message}</p>
              {/* Bubble tail — points down toward the presenter's mouth */}
              <span
                aria-hidden="true"
                className="absolute"
                style={{
                  left: "50%",
                  bottom: "-14px",
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: "12px solid transparent",
                  borderRight: "12px solid transparent",
                  borderTop: "18px solid #ffffff",
                  filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.15))",
                }}
              />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default Presenter;
